import type { InputFrame } from '../sim/World';

/* ═══════════════════════════════════════════════════════════════════════════
   Traduit clavier / souris / tactile en `InputFrame`.

   C'est la seule pièce du projet qui connaisse un périphérique. La simulation
   ne voit que des axes et des booléens — ce qui permet de la nourrir depuis un
   fichier d'entrées enregistré, sans qu'aucun périphérique n'existe (golden
   replays, attract mode, re-simulation serveur).

   ⚠️ Les événements sont accumulés dans un état, et `consume()` est appelé EN
   TÊTE DE TICK. Jamais l'inverse : lire l'entrée depuis les gestionnaires
   introduirait une dépendance à l'ordre d'arrivée des événements du
   navigateur, qui n'est pas déterministe.

   ── SOURIS ────────────────────────────────────────────────────────────────
   Le vaisseau SUIT le curseur, en absolu. C'est le schéma de Sky Force et de
   la plupart des shmups jouables à la souris, et il est nettement plus précis
   que le clavier — ce qui compte dans un genre où l'on esquive au pixel.

   Le mouvement est amorti par la même inertie que le clavier (`playerSystem`)
   plutôt qu'appliqué en téléportation : sans cela, la souris rendrait le
   dérapage impossible et casserait toutes les valeurs de feel calibrées au J0.

   ── TACTILE ───────────────────────────────────────────────────────────────
   Le doigt pilote en RELATIF : `pointerdown` mémorise l'origine, et chaque
   `pointermove` DÉPLACE la cible de la distance parcourue au lieu de la poser
   sous le doigt.

   L'absolu est inutilisable sur mobile, et pour une raison qu'aucun réglage ne
   rattrape : le doigt masque exactement l'objet qu'il faut regarder, et le
   point de hitbox — le seul pixel qui décide de la vie du joueur — devient
   invisible. En relatif, le pouce reste posé en bas de l'écran pendant que le
   vaisseau évolue au centre, entièrement visible.

   Le relatif écrit dans le MÊME `pointer` que la souris, donc dans le même
   `aimX`/`aimY` côté simulation. Un seul chemin d'entrée, une seule sensation,
   un seul format de replay.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Masques des boutons de souris, tels que produits par `1 << e.button`.

   ⚠️ Ils portent un nom parce que ces trois nombres étaient écrits en dur, et
   que DEUX D'ENTRE EUX ÉTAIENT INTERVERTIS : le focus lisait le bit 4 et l'arme
   secondaire le bit 2, si bien que le clic droit passait en mode focus et que
   l'arme secondaire exigeait la molette. Les commentaires alentour décrivaient
   pourtant le comportement voulu, et le `preventDefault` sur le menu contextuel
   — qui n'existe que pour libérer le clic droit — le confirmait.

   Le piège est que `MouseEvent.buttons`, l'attribut natif, emploie une TOUTE
   AUTRE convention : 1 = gauche, 2 = DROIT, 4 = milieu. Quiconque connaît cet
   attribut lit `& 2` comme « clic droit » et ne voit rien d'anormal. Les deux
   conventions ne se distinguent qu'au nom qu'on leur donne.

   Le bit 0 — clic gauche — sert désormais : le tir primaire n'est plus
   automatique (mécanique de CHAUFFE), on APPUIE pour tirer. */
const BTN_LEFT = 1 << 0;
const BTN_MIDDLE = 1 << 1;
const BTN_RIGHT = 1 << 2;

export interface PointerState {
  /** Cible visée, NORMALISÉE dans [-1, 1], origine au centre de l'écran.
   *  La conversion en unités monde appartient à l'appelant : lui seul connaît
   *  le cadrage de la caméra. */
  x: number;
  y: number;
  active: boolean;
}

/** Sensibilité par défaut du glissement tactile.
 *
 *  1,4 signifie que le vaisseau parcourt 40 % de plus que le doigt. La valeur
 *  se choisit entre deux échecs symétriques : sous 1,0 il faut balayer tout
 *  l'écran pour traverser le terrain, ce qu'un pouce ne peut pas faire sans
 *  lâcher le téléphone ; au-delà de 2,0 la correction fine devient un tirage
 *  au sort, alors qu'un shmup se joue au pixel près.
 *
 *  1,4 laisse le terrain entier accessible sur environ deux tiers d'écran de
 *  course, tout en gardant un rapport assez proche de 1:1 pour que les petits
 *  ajustements restent lisibles. */
export const TOUCH_SENSITIVITY = 1.4;

/** Vrai si le périphérique de pointage principal est grossier — un doigt.
 *  Exporté pour que l'appelant décide de monter ou non l'interface tactile
 *  sans dupliquer la requête média. */
export function isCoarsePointer(view: Window): boolean {
  return typeof view.matchMedia === 'function' && view.matchMedia('(pointer: coarse)').matches;
}

export class InputSource {
  private readonly keys = new Set<string>();
  private readonly frame: InputFrame = {
    moveX: 0,
    moveY: 0,
    focus: false,
    bomb: false,
    fire: true,
    secondary: false,
    ulti: false,
  };

  /** Cible visée par le pointeur, normalisée dans [-1, 1]. `active` reste faux
   *  tant qu'aucun pointeur n'a servi : le clavier est maître jusque-là. */
  readonly pointer: PointerState = { x: 0, y: 0, active: false };

  /** Sensibilité du glissement tactile, modifiable à chaud (menu d'options).
   *  Voir `TOUCH_SENSITIVITY` pour le raisonnement derrière la valeur. */
  touchSensitivity = TOUCH_SENSITIVITY;

  private detach: Array<() => void> = [];
  private mouseButtons = 0;


  /* ── Glissement relatif ───────────────────────────────────────────────
     `touchId` désigne le doigt QUI PILOTE, et lui seul. Sans ce verrou, le
     second contact — celui qui appuie sur une bombe, ou la paume qui effleure
     le bord — reprendrait la main et téléporterait la cible à chaque appui. */
  private touchId = -1;
  private touchX = 0;
  private touchY = 0;

  /* ── Zones tactiles ───────────────────────────────────────────────────
     Deux états par bouton, et ce n'est pas une redondance.

     `*Held` porte le maintien. `*Tap` retient qu'un appui A EU LIEU depuis la
     dernière lecture : une tape brève peut naître et mourir entre deux ticks,
     et `playerSystem` ne déclenche que sur FRONT MONTANT. Sans la mémoire,
     l'appui le plus rapide — celui d'un joueur qui panique, donc celui qui
     compte — serait purement et simplement perdu. */
  private padBombHeld = false;
  private padBombTap = false;
  private padSecondaryHeld = false;
  private padSecondaryTap = false;

  /** Fenêtre hôte, mémorisée à l'attache : les tests en fournissent une autre
   *  que la globale. */
  private host: Window | null = null;

  attach(target: Window = window, canvas?: HTMLElement): void {
    this.host = target;

    const down = (e: KeyboardEvent): void => {
      if (BLOCKED.has(e.key)) e.preventDefault();
      this.keys.add(e.key.toLowerCase());
    };
    const up = (e: KeyboardEvent): void => {
      this.keys.delete(e.key.toLowerCase());
    };
    // Un onglet qui perd le focus ne reçoit jamais le keyup : sans ça, le
    // vaisseau part en translation infinie au retour.
    const blur = (): void => {
      this.keys.clear();
      this.mouseButtons = 0;
      // Même panne côté tactile, en pire : un doigt encore posé au moment du
      // basculement d'onglet ne produira jamais son `pointerup`. Le pilotage
      // resterait attribué à un pointeur mort, et l'écran deviendrait inerte
      // sans qu'aucune erreur ne soit levée.
      this.touchId = -1;
      this.padBombHeld = false;
      this.padSecondaryHeld = false;
    };

    target.addEventListener('keydown', down);
    target.addEventListener('keyup', up);
    target.addEventListener('blur', blur);

    this.detach = [
      () => target.removeEventListener('keydown', down),
      () => target.removeEventListener('keyup', up),
      () => target.removeEventListener('blur', blur),
    ];

    if (canvas) this.attachPointer(canvas, target);
  }

  private attachPointer(canvas: HTMLElement, host: Window): void {
    /* ⚠️ `touch-action: none` est AUSSI posé ici, en plus de la feuille de
       style. Sans lui, le navigateur mobile réserve le premier mouvement au
       défilement, au zoom à deux doigts et au pull-to-refresh : le glissement
       est avalé avant d'atteindre le jeu, et la page rebondit pendant qu'on
       essaie d'esquiver. Le doubler en JS garantit la propriété même sur une
       page qui ne charge pas `ui/styles.css` (banc de charge, prototype). */
    canvas.style.touchAction = 'none';

    const move = (e: PointerEvent): void => {
      if (e.pointerType === 'touch') {
        // Un seul doigt pilote. Les autres sont ignorés ici même, avant tout
        // calcul : c'est ce qui empêche le second contact de voler la main.
        if (e.pointerId !== this.touchId) return;

        /* ⚠️ `clientWidth` et non `getBoundingClientRect()` : celui-ci alloue
           un DOMRect à CHAQUE événement, et un glissement en produit jusqu'à
           120 par seconde. Le relatif n'a besoin que de la taille, jamais de
           l'origine — la mesure sans allocation suffit.

           Repli sur la fenêtre tant que la mise en page n'a pas eu lieu : une
           largeur nulle donnerait une division par zéro, donc un NaN, et le
           vaisseau disparaîtrait sans la moindre erreur en console. */
        const w = canvas.clientWidth || host.innerWidth || 1;
        const h = canvas.clientHeight || host.innerHeight || 1;

        const dx = ((e.clientX - this.touchX) / w) * 2 * this.touchSensitivity;
        const dy = ((e.clientY - this.touchY) / h) * 2 * this.touchSensitivity;
        this.touchX = e.clientX;
        this.touchY = e.clientY;

        let x = this.pointer.x + dx;
        // L'axe Y du monde monte, celui de l'écran descend.
        let y = this.pointer.y - dy;

        /* Borné à l'écran. Sans cette borne, continuer de glisser au-delà du
           bord accumulerait une réserve invisible : le vaisseau resterait
           collé au bord pendant tout le trajet de retour, et le joueur
           croirait à un blocage du jeu. */
        if (x < -1) x = -1;
        else if (x > 1) x = 1;
        if (y < -1) y = -1;
        else if (y > 1) y = 1;

        this.pointer.x = x;
        this.pointer.y = y;
        return;
      }

      this.pointer.active = true;
      const r = canvas.getBoundingClientRect();
      // Normalisé dans [-1, 1], origine au centre — `main` le convertit en
      // unités monde, seul endroit qui connaisse le cadrage caméra.
      this.pointer.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      this.pointer.y = -(((e.clientY - r.top) / r.height) * 2 - 1);
    };

    const btnDown = (e: PointerEvent): void => {
      if (e.pointerType === 'touch') {
        // Premier arrivé, premier servi : tant que ce doigt n'a pas quitté
        // l'écran, aucun autre ne prend le pilotage.
        if (this.touchId !== -1) return;
        this.touchId = e.pointerId;
        this.touchX = e.clientX;
        this.touchY = e.clientY;
        this.pointer.active = true;
        /* Coupe le zoom par double tape, la sélection de texte et les
           événements souris de synthèse.
           ⚠️ Uniquement sur le CANVAS. Au niveau de la fenêtre, ce même appel
           tuerait les clics des écrans React, qui vivent au-dessus. */
        e.preventDefault();
        return;
      }

      this.mouseButtons |= 1 << e.button;
      // Le clic droit ouvre le menu contextuel par défaut, ce qui interrompt la
      // partie au moment précis où le joueur déclenche son arme secondaire.
      if (e.button === 2) e.preventDefault();
    };

    /* `pointerup` ET `pointercancel` mènent ici. Le second n'est pas un cas
       exotique sur mobile : le navigateur l'émet dès qu'il décide qu'un geste
       lui revient (barre système effleurée, appel entrant, rotation). Le
       traiter comme une fin de contact ordinaire est ce qui empêche le doigt
       fantôme — un pointeur qui n'existe plus mais détient encore le pilotage.

       La cible visée, elle, N'EST PAS remise à zéro : elle reste là où le doigt
       l'a laissée. Le vaisseau la rejoint puis s'immobilise dans la zone morte
       de `playerSystem`. La remettre au centre ferait au contraire traverser
       l'écran au vaisseau à chaque doigt levé — c'est ça, la dérive. */
    const release = (e: PointerEvent): void => {
      if (e.pointerType === 'touch') {
        if (e.pointerId === this.touchId) this.touchId = -1;
        return;
      }
      this.mouseButtons &= ~(1 << e.button);
    };

    const ctx = (e: Event): void => e.preventDefault();

    // `passive: false` explicite : sans lui, un navigateur qui déciderait
    // d'appliquer son défaut passif à ces événements ignorerait silencieusement
    // le `preventDefault` ci-dessus.
    const opts: AddEventListenerOptions = { passive: false };
    canvas.addEventListener('pointermove', move, opts);
    canvas.addEventListener('pointerdown', btnDown, opts);
    host.addEventListener('pointerup', release);
    host.addEventListener('pointercancel', release);
    canvas.addEventListener('contextmenu', ctx);

    this.detach.push(
      () => canvas.removeEventListener('pointermove', move),
      () => canvas.removeEventListener('pointerdown', btnDown),
      () => host.removeEventListener('pointerup', release),
      () => host.removeEventListener('pointercancel', release),
      () => canvas.removeEventListener('contextmenu', ctx),
    );
  }

  /**
   * Monte les deux zones tactiles — bombe et arme secondaire — et les branche
   * sur le MÊME `InputFrame` que le clavier et la souris.
   *
   * Rien n'est créé si le pointeur n'est pas grossier : sur desktop, ni nœud
   * DOM, ni écouteur, ni coût.
   *
   * @param parent  Conteneur, en général `document.body`.
   * @param enabled Force la décision. Par défaut, `(pointer: coarse)`.
   * @returns Vrai si les boutons ont été montés.
   */
  attachTouchButtons(parent: HTMLElement, enabled?: boolean): boolean {
    const doc = parent.ownerDocument;
    const view = this.host ?? doc.defaultView;
    if (!(enabled ?? (view !== null && isCoarsePointer(view)))) return false;

    const pad = doc.createElement('div');
    pad.className = 'touch-pad';

    const mk = (kind: 'bomb' | 'secondary', label: string, glyph: string): void => {
      const b = doc.createElement('button');
      b.type = 'button';
      b.className = `touch-btn touch-${kind}`;
      b.textContent = glyph;
      b.setAttribute('aria-label', label);

      const press = (e: PointerEvent): void => {
        // Sans `preventDefault`, un maintien déclenche la sélection de texte et
        // le menu contextuel d'appui long d'Android, en plein milieu d'une
        // vague.
        e.preventDefault();
        if (kind === 'bomb') {
          this.padBombHeld = true;
          this.padBombTap = true;
        } else {
          this.padSecondaryHeld = true;
          this.padSecondaryTap = true;
        }
      };
      const lift = (): void => {
        if (kind === 'bomb') this.padBombHeld = false;
        else this.padSecondaryHeld = false;
      };

      b.addEventListener('pointerdown', press, { passive: false });
      b.addEventListener('pointerup', lift);
      b.addEventListener('pointercancel', lift);
      b.addEventListener('pointerleave', lift);
      pad.appendChild(b);
    };

    mk('bomb', 'Bombe', '◉');
    mk('secondary', 'Arme secondaire', '✦');

    parent.appendChild(pad);
    // Les écouteurs meurent avec leurs nœuds : retirer le conteneur suffit.
    this.detach.push(() => pad.remove());
    return true;
  }

  dispose(): void {
    for (const d of this.detach) d();
    this.detach = [];
    this.keys.clear();
    this.touchId = -1;
    this.padBombHeld = false;
    this.padBombTap = false;
    this.padSecondaryHeld = false;
    this.padSecondaryTap = false;
  }

  /**
   * Recale la cible visée sur une position connue, en coordonnées normalisées.
   *
   * À appeler au (re)démarrage d'une run : sans cela, le premier glissement
   * part du centre de l'écran alors que le vaisseau apparaît en bas, et le
   * joueur voit son appareil filer vers le haut sans l'avoir demandé.
   *
   * Sans effet tant qu'un doigt pilote — recaler sous le doigt produirait
   * exactement le saut que le mode relatif existe pour éviter.
   */
  syncAim(nx: number, ny: number): void {
    if (this.touchId !== -1) return;
    this.pointer.x = nx < -1 ? -1 : nx > 1 ? 1 : nx;
    this.pointer.y = ny < -1 ? -1 : ny > 1 ? 1 : ny;
  }

  /** L'objet retourné est RÉUTILISÉ d'un tick à l'autre : aucune allocation
   *  dans la boucle. L'appelant ne doit pas le conserver. */
  consume(): InputFrame {
    const k = this.keys;
    const f = this.frame;

    f.moveX =
      (k.has('arrowright') || k.has('d') ? 1 : 0) - (k.has('arrowleft') || k.has('a') ? 1 : 0);
    f.moveY = (k.has('arrowup') || k.has('w') ? 1 : 0) - (k.has('arrowdown') || k.has('s') ? 1 : 0);

    // Maj au clavier, molette enfoncée à la souris.
    f.focus = k.has('shift') || (this.mouseButtons & BTN_MIDDLE) !== 0;

    // Tir primaire MANUEL : clic gauche à la souris, ou touche « J » au
    // clavier pur. Le tir automatique historique (§6.3) a cédé la place à la
    // mécanique de CHAUFFE — c'est justement la retenue du joueur qui empêche la
    // surchauffe, donc marteler la gâchette redevient un choix, pas une corvée
    // subie. Au TACTILE, tout doigt qui pilote tire : sur mobile il n'y a pas de
    // gâchette dédiée, et « je bouge donc je tire » reste lisible (on lève le
    // doigt pour laisser refroidir).
    f.fire =
      (this.mouseButtons & BTN_LEFT) !== 0 || k.has('j') || this.touchId !== -1;

    // Clic droit = arme SECONDAIRE. Espace = bombe. Au doigt, les deux zones
    // tactiles alimentent ces mêmes champs — pas un second chemin d'entrée.
    //
    // ⚠️ Ces deux-là ne doivent surtout pas être liés. Une première version
    // faisait `f.bomb = k.has(' ') || f.secondary`, ce qui consommait une bombe
    // à chaque tir secondaire : le joueur voyait son stock fondre sans
    // comprendre pourquoi, et la bombe — sa sortie de secours — disparaissait
    // au pire moment.
    f.secondary =
      (this.mouseButtons & BTN_RIGHT) !== 0 || this.padSecondaryHeld || this.padSecondaryTap;
    f.bomb = k.has(' ') || this.padBombHeld || this.padBombTap;
    // ULTIME — touche E. Se déclenche au front (jauge pleine) côté simulation.
    f.ulti = k.has('e');

    // La mémoire de tape est consommée ici, et une seule fois : elle vaut
    // exactement un tick, la durée d'un front montant.
    this.padBombTap = false;
    this.padSecondaryTap = false;

    return f;
  }
}

const BLOCKED = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']);
